"use client";

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslations } from "next-intl";
import { HelpCircle } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  getInAppBrowserType,
  getOpenExternalPlan,
  copyToClipboard,
  type InAppBrowserType,
} from "@/lib/utils/in-app-browser";
import { redirectExternalWithFallback } from "@/lib/auth/external-redirect";
import { InAppBrowserGuide } from "@/components/auth/InAppBrowserGuide";

const DISMISS_KEY = "locarora.inAppBrowserBannerDismissed";
const DISMISS_TTL_MS = 5 * 60 * 1000; // 5분 — 영구 dismiss 방지

/**
 * 인앱 브라우저(KakaoTalk, Line, Instagram, Facebook, Threads, Twitter, Naver 등)
 * 접속 사용자에게 외부 브라우저로 열도록 안내하는 배너.
 *
 * 동작:
 * - auto-redirect 가능 환경(Android 전체, iOS KakaoTalk/LINE/Naver):
 *   "외부 브라우저에서 열기" 버튼 클릭 시 즉시 시스템 브라우저로 이동
 * - manual-guide 환경(iOS Instagram/Facebook/Threads/Twitter):
 *   Apple 정책상 코드 강제 이동 불가 → 풀스크린 가이드 모달 노출
 *
 * SSR 하이드레이션 안전:
 * - 초기 상태는 항상 `false` (서버와 일치)
 * - 클라이언트 mount 후 `useEffect`에서 감지 실행
 * - Capacitor 네이티브 앱은 감지 유틸에서 자동 제외
 */
interface DetectionState {
  show: boolean;
  browserType: InAppBrowserType;
  canAutoRedirect: boolean;
}

const HIDDEN: DetectionState = {
  show: false,
  browserType: "unknown",
  canAutoRedirect: false,
};

export function InAppBrowserBanner() {
  const t = useTranslations("common.auth.inAppBrowser");
  const [detection, setDetection] = useState<DetectionState>(HIDDEN);
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    const type = getInAppBrowserType();
    if (type === "unknown") return;

    try {
      const dismissed = sessionStorage.getItem(DISMISS_KEY);
      if (dismissed) {
        const ts = Number(dismissed);
        if (Number.isFinite(ts) && Date.now() - ts < DISMISS_TTL_MS) return;
      }
    } catch {
      // sessionStorage 차단 브라우저 — dismiss 상태 유실 수용
    }

    const plan = getOpenExternalPlan(window.location.href);
    // 마운트 시점 1회 브라우저 환경 감지 — useEffect setState는 SSR 안전 하이드레이션을
    // 위해 의도적이며, 의존성 외 외부 변화를 추적하지 않는다.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDetection({
      show: true,
      browserType: type,
      canAutoRedirect: plan?.type === "auto-redirect",
    });
  }, []);

  if (!detection.show) return null;

  const handleOpenExternal = () => {
    const plan = getOpenExternalPlan(window.location.href);
    if (plan?.type === "auto-redirect" && plan.url) {
      redirectExternalWithFallback(plan.url, () => setGuideOpen(true));
    } else {
      setGuideOpen(true);
    }
  };

  const handleCopy = async () => {
    const ok = await copyToClipboard(window.location.href);
    if (ok) {
      toast.success(t("copied"));
    } else {
      toast.error(t("copyFailed"));
    }
  };

  const handleDismiss = () => {
    setDetection(HIDDEN);
    try {
      sessionStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // ignore
    }
  };

  return (
    <>
      <div
        role="alert"
        className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="font-semibold text-amber-900">{t("title")}</p>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="text-amber-700 hover:text-amber-900"
                    aria-label={t("whyAriaLabel")}
                    tabIndex={-1}
                  >
                    <HelpCircle className="h-3.5 w-3.5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  side="bottom"
                  align="start"
                  className="w-64 text-xs leading-relaxed"
                >
                  {t("whyExplanation")}
                </PopoverContent>
              </Popover>
            </div>
            <p className="mt-1 text-amber-800">{t("description")}</p>

            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleOpenExternal}
                className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700"
              >
                {detection.canAutoRedirect ? t("openBrowser") : t("howToOpen")}
              </button>
              <button
                type="button"
                onClick={handleCopy}
                className="rounded-md border border-amber-400 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100"
              >
                {t("copyLink")}
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={handleDismiss}
            aria-label={t("dismiss")}
            className="shrink-0 text-amber-700 hover:text-amber-900"
            tabIndex={-1}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      <InAppBrowserGuide
        open={guideOpen}
        onOpenChange={setGuideOpen}
        browserType={detection.browserType}
      />
    </>
  );
}
